<template>
  <div class="legend">
    <div class="legend--mobile">
      <UtilitySlider
        variant="legend"
        :items="labels"
        :unselectedItems="unselectedLabels"
        :colors="colors"
        @highlightedItem="highlightLabel"
        @downplayedItem="downplayLabel"
        @toggledItem="toggleLabel"
      />
    </div>

    <div class="legend--desktop">
      <UtilityScroll :height="getScrollHeight()">
        <div class="legend__header">
          <p>
            Labels
            <span v-if="unselectedLabels.length === 0">({{ labels.length }})</span>
            <span v-else>({{ labels.length - unselectedLabels.length }}/{{ labels.length }})</span>
          </p>
        </div>

        <div class="legend__items">
          <div
            v-for="label in labels"
            :key="'LABEL__' + label.name"
            :class="['legend__item', unselectedLabels.includes(label.name) ? 'legend__item--inactive' : 'legend__item--active']"
            @mouseover="highlightLabel(label.name)"
            @mouseout="downplayLabel(label.name)"
            @click="toggleLabel(label.name)"
          >
            <div
              class="legend__icon"
              :style="'background-color: ' + label.color + ';'"
            />
            <div
              class="legend__name"
              v-html="highlight(label.name, filterWords)"
            />
          </div>
        </div>
      </UtilityScroll>
    </div>
  </div>
</template>

<script>
import UtilitySlider from '@/components/utility/Slider.vue'
import UtilityScroll from '@/components/utility/Scroll.vue'
import helperMethods from '@/helpers/utility.js'

export default {
  components: {
    UtilitySlider,
    UtilityScroll
  },
  props: {
    unselectedLabels: {
      type: Array,
      default () {
        return []
      }
    }
  },
  data() {
    return {
      windowWidth: window.innerWidth,
      colors: [
        '#1593A6',
        '#8BC8D3',
        '#6BBB5D',
        '#BCDE85',
        '#E18731',
        '#F5AC91',
        '#A86CC5',
        '#B4ACE3',
        '#DF5D99',
        '#DEA1D1',
        '#48AA9F',
        '#96D1B4',
        '#EE7447',
        '#F4A3A0'
      ]
    }
  },
  computed: {
    results () {
      return this.$store.getters['search/getResults'].dataset
    },
    selectedHash () {
      return this.$store.getters['search/getSelectedHash']
    },
    labels () {
      let labels = []
      let i = 0

      if (this.results && this.results.byId[this.selectedHash]) {
        let dataSeries = this.results.byId[this.selectedHash].attributes.data_series

        for (const [key, value] of Object.entries(dataSeries)) {
          let colorIndex = i

          while (colorIndex >= this.colors.length) {
            if (colorIndex >= this.colors.length) {
              colorIndex = colorIndex - this.colors.length
            }
          }

          labels.push({
            name: key,
            value: dataSeries[key],
            color: this.colors[colorIndex]
          })

          i++
        }
      }

      return labels
    },
    filterWords () {
      let words = []
      let getter = this.$store.getters['search/getSelectedFilters']

      Object.keys(getter).forEach(function (filters) {
        if (getter[filters] && getter[filters].length > 0) {
          getter[filters].forEach(function (filter) {
            words.push(filter.attributes.name)
          })
        }
      })

      return words
    }
  },
  methods: {
    toggleLabel (key) {
      if (typeof key === 'object') {
        this.$emit('labelToggled', key.name)
      } else {
        this.$emit('labelToggled', key)
      }
    },
    highlightLabel (key) {
      this.$emit('labelHighlighted', key)
    },
    downplayLabel (key) {
      this.$emit('labelDownplayed', key)
    },
    getScrollHeight () {
      let height = 'auto'

      if (this.windowWidth >= 960) {
        height = '502px'

        if (this.windowWidth >= 1200) {
          height = '542px'

          if (this.windowWidth >= 1600) {
            height = '582px'
          }
        }
      }

      return height
    },
    resize () {
      this.windowWidth = window.innerWidth
    },
    ...helperMethods
  },
  created () {
    window.addEventListener('resize', this.resize)
  },
  unmounted () {
    window.removeEventListener('resize', this.resize)
  },
  mounted () {
    this.resize()
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/app.scss';

.legend {
  @include font('content--md');
  width: 100%;
  overflow: auto;
  background-color: $bg-2;

  @media screen and (min-width: $breakpointLg) {
    @include font('content--lg');
  }

  &--mobile {
    display: block;
    height: auto;
    border-top: 1px solid rgba($white, 0.1);

    @media screen and (min-width: $breakpointMd) {
      display: none;
    }
  }

  &--desktop {
    position: relative;
    //left: -1px;
    //height: 460px;
    border-left: 1px solid rgba($white, 0.1);
    display: none;

    @media screen and (min-width: $breakpointMd) {
      height: 500px;
      display: block;
    }

    @media screen and (min-width: $breakpointLg) {
      height: 540px;
    }

    @media screen and (min-width: $breakpointXl) {
      height: 580px;
    }
  }

  &__header {
    background-color: $bg-2;
    //border-left: 1px solid rgba($white, 0.1);
    border-bottom: 1px solid $black;

    p {
      color: rgba($white, 0.5);
      margin: 0;
      padding: 0.25rem 0.5rem;
      text-transform: uppercase;
    }

    span {
      color: rgba($white, 0.7);
    }
  }

  &__items {
    border-bottom: 1px solid rgba($white, 0.1);
  }

  &__item {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 2px 0px;
    border-top: 1px solid rgba($white, 0.1);
    //border-left: 1px solid rgba($white, 0.1);
    border-bottom: 1px solid $black;
    user-select: none;
    cursor: pointer;

    &--active {
      background-color: $bg-2;

      &:hover, &:active, &:focus {
        background-color: $bg-4;

        .legend__icon {
          transform: scale(1.15);
        }
      }
    }

    &--inactive {
      background-color: $bg-4;
      color: rgba($white, 0.5);

      &:hover, &:active, &:focus {
        color: rgba($white, 0.8);
      }

      .legend__icon {
        background-color: rgba($white, 0.25) !important;
      }
    }
  }

  &__icon {
    background-color: $blue;
    width: 1rem;
    height: 1rem;
    margin: 0.25rem 0.5rem;
    border-radius: 4px;
    transition: transform 0.2s, background-color 0.2s;
  }

  &__name {
    flex: 1;
    margin-right: 0.5rem;
  }
}

</style>